/**
 * Read-only display for the `booking_questions` property field type.
 *
 * Parses the stored JSON via the shared `parseBookingQuestions` helper and
 * renders a compact summary — one line per question with its type badge and
 * a textual "Required" indicator — rather than dumping the raw JSON string.
 *
 * Used by `PropertyValueLabel.tsx` when `field_type_name === 'booking_questions'`.
 */

import { useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import {
  parseBookingQuestions,
  QUESTION_TYPE_OPTIONS,
} from '@/lib/crm/utils/bookingQuestions';

interface BookingQuestionsValueLabelProps {
  /** Stored value — either the raw JSON string from the property, or an already-parsed value */
  value: unknown;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** When true, collapse to a single line with "+N more" affordance for tables/inline use */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

const QUESTION_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  QUESTION_TYPE_OPTIONS.map(({ id, label }) => [id, label])
);

export const BookingQuestionsValueLabel: React.FC<
  BookingQuestionsValueLabelProps
> = ({ value, fontSize = 'sm', className = '', truncate }) => {
  const sizeClass = fontSizeClasses[fontSize];

  const questions = useMemo(() => parseBookingQuestions(value), [value]);

  if (questions.length === 0) {
    return (
      <span
        className={`${sizeClass} ${className}`}
        title='No booking questions configured'
      >
        —
      </span>
    );
  }

  if (truncate) {
    const first = questions[0];
    const remaining = questions.length - 1;
    const fullTitle = questions.map(q => q.label).join(' · ');
    return (
      <span
        className={`${sizeClass} block truncate ${className}`}
        title={fullTitle}
      >
        {first.label}
        {remaining > 0 ? ` · +${remaining} more` : ''}
      </span>
    );
  }

  return (
    <div className={`${sizeClass} flex flex-col gap-1 ${className}`}>
      {questions.map(question => (
        <div key={question.key} className='flex items-center gap-2'>
          <span className='text-[var(--color-text-primary)]'>
            {question.label}
          </span>
          <Badge color='gray' size='sm'>
            {QUESTION_TYPE_LABELS[question.type] ?? question.type}
          </Badge>
          {question.required && (
            <span className='text-xs text-[var(--color-text-secondary)]'>
              Required
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
